import React, { useState } from 'react';
import { Pagination } from 'flowbite-react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp } from 'lucide-react';
import Skeleton from '../skeleton';

interface Column {
  header: string;
  accessor: string;
  cell?: (row: any, index?: number) => React.ReactNode;
  sortingField?: string;
}

interface PaginationInfo {
  page: number;
  totalPages: number;
  [key: string]: any;
}

interface IResponse {
  pagination?: PaginationInfo;
  data: any[];
}

type ISorting = {
  sortingField: string;
  sortingOrder: string;
};

interface DataTableProps {
  columns: Column[];
  response: IResponse;
  onPageChange?: (page: number) => void;
  onShort?: (sorting: ISorting) => void;
  loading: boolean;

  /** Optional Accordion Renderer */
  renderAccordion?: (row?: any, index?: number) => React.ReactNode;
}

const DataTablePresentation: React.FC<DataTableProps> = ({
  columns,
  response,
  onPageChange,
  loading,
  onShort,
  renderAccordion
}) => {
  const [sortingField, setSortingField] = useState<ISorting>({
    sortingField: 'dateCreated',
    sortingOrder: 'desc'
  });

  /** 🔥 Multiple expanded rows */
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { data = [], pagination = { page: 1, totalPages: 1 } } = response || {};

  const handleSort = (field: string, order: string) => {
    onShort?.({ sortingField: field, sortingOrder: order });
    setSortingField({ sortingField: field, sortingOrder: order });
  };

  /** Toggle single row */
  const toggleRow = (index: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  /** Toggle all rows */
  const toggleAllRows = () => {
    if (expandedRows.size === data.length) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(data.map((_, i) => i)));
    }
  };

  return (
    <>
      <div className=" overflow-x-auto dark:bg-darkgray">
        <table className="min-w-full text-sm text-left text-gray-500 dark:bg-darkgray">
          {/* ================= HEADER ================= */}
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-darkgray sticky top-0">
            <tr>
              {renderAccordion && (
                <th className="px-3 py-3 w-10">
                  <button onClick={toggleAllRows}>
                    {expandedRows.size === data.length ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                </th>
              )}

              {columns.map((col) => (
                <th key={col.accessor} className="px-6 py-3">
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortingField && (
                      <>
                        {col.sortingField === sortingField.sortingField ? (
                          sortingField.sortingOrder === 'desc' ? (
                            <ArrowUp
                              size={14}
                              className="cursor-pointer"
                              onClick={() => handleSort(col.sortingField!, 'asc')}
                            />
                          ) : (
                            <ArrowDown
                              size={14}
                              className="cursor-pointer"
                              onClick={() => handleSort(col.sortingField!, 'desc')}
                            />
                          )
                        ) : (
                          <ArrowDown
                            size={14}
                            className="cursor-pointer"
                            onClick={() => handleSort(col.sortingField!, 'desc')}
                          />
                        )}
                      </>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* ================= BODY ================= */}
          <tbody>
            {loading ? (
              Array(10).fill(null).map((_, i) => (
                <tr key={i}>
                  <td colSpan={columns.length + 1} className="px-6 py-4">
                    <Skeleton className="w-full h-10" />
                  </td>
                </tr>
              ))
            ) : data.length > 0 ? (
              data.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>

                  {/* ===== MAIN ROW ===== */}
                  <tr
                    className={`border-b bg-white dark:bg-darkgray transition-colors
                      ${renderAccordion ? 'cursor-pointer hover:bg-gray-50' : ''}
                    `}
                  >
                    {renderAccordion && (
                      <td
                        className="px-3 py-4"
                        onClick={() => toggleRow(rowIndex)}
                      >
                        {expandedRows.has(rowIndex) ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </td>
                    )}

                    {columns.map((col) => (
                      <td key={col.accessor} className="px-6 py-4">
                        {col.cell ? col.cell(row, rowIndex) : row[col.accessor]}
                      </td>
                    ))}
                  </tr>

                  {/* ===== ACCORDION ROW ===== */}
                  {renderAccordion && expandedRows.has(rowIndex) && (
                    <tr className="bg-gray-50 dark:bg-darkgray">
                      <td colSpan={columns.length + 1} className="px-6 py-4">
                        {renderAccordion(row, rowIndex)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="text-center py-6 text-gray-500 dark:text-gray-300"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ================= PAGINATION ================= */}
      {onPageChange && (
        <div className="flex justify-end mt-4">
          <Pagination
            currentPage={pagination.page}
            onPageChange={onPageChange}
            totalPages={pagination.totalPages}
          />
        </div>
      )}
    </>
  );
};

export default DataTablePresentation;
